/* Vauban add-on bar — live threshold state.
   Listens for cart updates and toggles the --reached class without page reload. */

if (!customElements.get('vauban-addon-bar')) {
  class VaubanAddonBar extends HTMLElement {
    static moneyFormatters = {};

    connectedCallback() {
      this.thresholdMap = {
        CZK: Number(this.getAttribute('threshold-czk') || 5500) * 100,
        EUR: Number(this.getAttribute('threshold-eur') || 220) * 100,
        PLN: Number(this.getAttribute('threshold-pln') || 950) * 100,
      };
      this.currency = this.getAttribute('cart-currency') || 'CZK';
      this.unreachedTitle = this.querySelector('.vauban-addon__title--unreached');
      this.reachedTitle = this.querySelector('.vauban-addon__title--reached');
      this.progressBar = this.querySelector('.vauban-addon__progress-bar');
      this.moneySpan = this.querySelector('[data-money]');

      this._onCartChange = this.refresh.bind(this);
      document.addEventListener('cart:refresh', this._onCartChange);
      document.addEventListener('cart:updated', this._onCartChange);
      VaubanAddonBar.installFetchInterceptor();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this._onCartChange);
      document.removeEventListener('cart:updated', this._onCartChange);
    }

    static installFetchInterceptor() {
      if (window.__vaubanAddonFetchInstalled) return;
      window.__vaubanAddonFetchInstalled = true;
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (input, init) => {
        const response = await originalFetch(input, init);
        try {
          const url = typeof input === 'string' ? input : input?.url;
          const method = (init?.method || 'GET').toUpperCase();
          if (url && /\/cart\/(add|change|update|clear)(\.js)?/.test(url) && method !== 'GET') {
            response.clone().json().then(() => {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }).catch(() => {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            });
          }
        } catch (_) {}
        return response;
      };
    }

    async refresh() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const currency = cart.currency || this.currency;
        const threshold = this.thresholdMap[currency] || this.thresholdMap.CZK;
        const total = Number(cart.total_price) || 0;
        const reached = total >= threshold;
        this.classList.toggle('vauban-addon--reached', reached);

        if (this.unreachedTitle) this.unreachedTitle.hidden = reached;
        if (this.reachedTitle) this.reachedTitle.hidden = !reached;

        if (this.progressBar) {
          const pct = reached ? 100 : Math.min(100, Math.round((total / threshold) * 100));
          this.progressBar.style.width = pct + '%';
        }

        if (this.moneySpan && !reached) {
          const remaining = Math.max(0, threshold - total);
          this.moneySpan.textContent = this.formatMoney(remaining, currency);
        }
      } catch (e) {
        /* Network fail — keep current state. */
      }
    }

    formatMoney(cents, currency) {
      try {
        if (!VaubanAddonBar.moneyFormatters[currency]) {
          VaubanAddonBar.moneyFormatters[currency] = new Intl.NumberFormat(document.documentElement.lang || 'cs-CZ', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0,
          });
        }
        return VaubanAddonBar.moneyFormatters[currency].format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(0) + ' ' + currency;
      }
    }
  }

  customElements.define('vauban-addon-bar', VaubanAddonBar);
}

/* =====================================================================
   Inline variant picker — updates form's hidden id input and prices
   ===================================================================== */
if (!window.__vaubanInlinePickerInstalled) {
  window.__vaubanInlinePickerInstalled = true;

  function getCurrency() {
    const bar = document.querySelector('vauban-addon-bar[cart-currency]');
    return (bar && bar.getAttribute('cart-currency')) || 'CZK';
  }

  function formatMoney(amountCents, currency) {
    const amount = amountCents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'cs-CZ', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_) {
      return Math.round(amount) + ' ' + currency;
    }
  }

  function selectVariant(btn) {
    const card = btn.closest('[data-vauban-card]');
    if (!card) return;
    const variantId = btn.getAttribute('data-vauban-variant');
    const priceCents = Number(btn.getAttribute('data-vauban-variant-price')) || 0;
    const available = btn.getAttribute('data-vauban-variant-available') === 'true';
    if (!available) return;

    // Toggle selected state across the group
    card.querySelectorAll('[data-vauban-variant]').forEach((b) => {
      const isThis = b === btn;
      b.classList.toggle('is-selected', isThis);
      b.setAttribute('aria-checked', isThis ? 'true' : 'false');
    });

    // Update hidden form input
    const input = card.querySelector('[data-vauban-variant-input]');
    if (input) input.value = variantId;

    // Update displayed prices
    const currency = getCurrency();
    const origEl = card.querySelector('[data-vauban-price-original]');
    const discEl = card.querySelector('[data-vauban-price-discounted]');
    if (origEl) origEl.textContent = formatMoney(priceCents, currency);
    if (discEl) discEl.textContent = formatMoney(priceCents / 2, currency);
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-vauban-variant]');
    if (!btn) return;
    e.preventDefault();
    selectVariant(btn);
  });

  // Keyboard support: arrows + space/enter
  document.addEventListener('keydown', (e) => {
    const btn = e.target.closest && e.target.closest('[data-vauban-variant]');
    if (!btn) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      selectVariant(btn);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = btn.nextElementSibling || btn.parentElement.firstElementChild;
      next && next.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = btn.previousElementSibling || btn.parentElement.lastElementChild;
      prev && prev.focus();
    }
  });
}

